import React, { useState } from 'react';
import { Plus, Trash2, ChevronDown, ChevronUp, Music, Users, Heart, ArrowUp, ArrowDown } from 'lucide-react';

const CEREMONY_TYPES = [
  { value: 'church', label: 'Kirkebryllup' },
  { value: 'civil', label: 'Civil vielse' },
  { value: 'outdoor', label: 'Outdoorseremoni' },
  { value: 'symbolic', label: 'Symbolsk seremoni' },
  { value: 'cultural', label: 'Cultural/religious' },
  { value: 'other', label: 'Other' },
];

const defaultProcessionEntry = () => ({
  id: `proc-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`,
  who: '', withWhom: '', music: '', notes: '',
});

const defaultReading = () => ({
  id: `read-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`,
  title: '', readBy: '', text: '', notes: '',
});

const defaultRitual = () => ({
  id: `rit-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`,
  name: '', description: '', responsible: '', notes: '',
});

const Inp = ({ label, value, onChange, placeholder, className = '' }) => (
  <div className={className}>
    <label className="text-[9px] font-sans font-semibold text-stone-500 uppercase tracking-wide mb-0.5 block">{label}</label>
    <input value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      className="w-full text-xs font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
  </div>
);

const Section = ({ title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-4 text-left">
        <h4 className="text-xs font-sans font-semibold text-stone-700 uppercase tracking-wide">{title}</h4>
        {open ? <ChevronUp className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
      </button>
      {open && <div className="px-4 pb-4 space-y-3 border-t border-stone-50">{children}</div>}
    </div>
  );
};

export const CeremonyPlanner = ({ ceremony, onChange, couple }) => {
  const update = (field, value) => onChange({ ...ceremony, [field]: value });

  const procession = ceremony?.procession || [];
  const readings = ceremony?.readings || [];
  const rituals = ceremony?.rituals || [];

  const updateList = (key, list) => onChange({ ...ceremony, [key]: list });

  const addToList = (key, factory) => updateList(key, [...(ceremony?.[key] || []), factory()]);
  const removeFromList = (key, idx) => updateList(key, (ceremony?.[key] || []).filter((_, i) => i !== idx));
  const updateListItem = (key, idx, field, value) => {
    const list = [...(ceremony?.[key] || [])];
    list[idx] = { ...list[idx], [field]: value };
    updateList(key, list);
  };
  const moveListItem = (key, idx, dir) => {
    const list = [...(ceremony?.[key] || [])];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= list.length) return;
    [list[idx], list[newIdx]] = [list[newIdx], list[idx]];
    updateList(key, list);
  };

  const p1 = couple?.person1Name || 'Person 1';
  const p2 = couple?.person2Name || 'Person 2';

  return (
    <div data-testid="ceremony-planner" className="space-y-4">
      <div>
        <h3 className="font-sans font-semibold text-stone-800 text-sm">Ceremony</h3>
        <p className="text-xs font-sans text-stone-500 mt-0.5">Plan the ceremony in detail</p>
        <p className="text-[10px] font-sans text-stone-400 mt-1 leading-relaxed">
          Choose ceremony type, set up entrance order, vows and any readings or rituals. Everything is optional — customize to suit your style.
        </p>
      </div>

      {/* Ceremony type */}
      <div className="bg-white rounded-2xl border border-stone-100 p-4">
        <label className="text-xs font-sans font-medium text-stone-600 mb-2 block">Ceremony-type</label>
        <div className="flex flex-wrap gap-2">
          {CEREMONY_TYPES.map(t => (
            <button key={t.value} type="button" onClick={() => update('ceremonyType', t.value)}
              className={`text-xs font-sans px-3 py-2 rounded-xl border transition-all ${
                ceremony?.ceremonyType === t.value ? 'bg-primary/10 border-primary text-primary font-semibold' : 'bg-white border-stone-200 text-stone-600 hover:border-stone-400'
              }`}>{t.label}</button>
          ))}
        </div>
      </div>

      {/* Officiant */}
      <div className="bg-white rounded-2xl border border-stone-100 p-4 space-y-2">
        <h4 className="text-[10px] font-sans font-bold text-stone-500 uppercase tracking-wide">Officiant / Offisiant</h4>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <Inp label="Navn" value={ceremony?.officiantName} onChange={v => update('officiantName', v)} placeholder="Officiant" />
          <Inp label="Phone" value={ceremony?.officiantPhone} onChange={v => update('officiantPhone', v)} placeholder="Phone" />
          <Inp label="Email" value={ceremony?.officiantEmail} onChange={v => update('officiantEmail', v)} placeholder="Email" />
        </div>
      </div>

      {/* Procession */}
      <Section title="Entrancesorder" defaultOpen>
        <p className="text-[10px] font-sans text-stone-400 mb-2">Who enters, with whom, and to which music?</p>
        {procession.map((entry, idx) => (
          <div key={entry.id} className="bg-stone-50/80 rounded-lg p-2.5 space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-sans font-bold text-stone-400 w-5 text-center">{idx + 1}</span>
              <input value={entry.who} onChange={e => updateListItem('procession', idx, 'who', e.target.value)} placeholder="Hvem?"
                className="flex-1 text-xs font-sans px-2 py-1.5 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
              <input value={entry.withWhom} onChange={e => updateListItem('procession', idx, 'withWhom', e.target.value)} placeholder="Med hvem?"
                className="flex-1 text-xs font-sans px-2 py-1.5 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
              <div className="flex items-center gap-0.5">
                <button type="button" onClick={() => moveListItem('procession', idx, -1)} disabled={idx === 0} className="p-0.5 text-stone-300 hover:text-stone-600 disabled:opacity-20">
                  <ArrowUp className="w-3 h-3" />
                </button>
                <button type="button" onClick={() => moveListItem('procession', idx, 1)} disabled={idx === procession.length - 1} className="p-0.5 text-stone-300 hover:text-stone-600 disabled:opacity-20">
                  <ArrowDown className="w-3 h-3" />
                </button>
              </div>
              <button type="button" onClick={() => removeFromList('procession', idx)} className="text-stone-300 hover:text-red-500 transition-colors">
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 ml-7">
              <div className="flex items-center gap-1">
                <Music className="w-3 h-3 text-stone-400" />
                <input value={entry.music} onChange={e => updateListItem('procession', idx, 'music', e.target.value)} placeholder="Musikk"
                  className="flex-1 text-[10px] font-sans px-2 py-1 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
              </div>
              <input value={entry.notes} onChange={e => updateListItem('procession', idx, 'notes', e.target.value)} placeholder="Notes"
                className="text-[10px] font-sans px-2 py-1 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
            </div>
          </div>
        ))}
        <button type="button" onClick={() => addToList('procession', defaultProcessionEntry)}
          className="flex items-center gap-1 text-xs font-sans text-stone-500 hover:text-primary transition-colors">
          <Plus className="w-3 h-3" /> Add i prosesjon
        </button>
      </Section>

      {/* Vows */}
      <Section title="Vows & Signing">
        <div className="space-y-2">
          <div>
            <label className="text-xs font-sans font-medium text-stone-600 mb-1 block">Vows</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {['Standard (officiant)', 'Personal', 'Mixed', 'None'].map(opt => (
                <button key={opt} type="button" onClick={() => update('vowType', opt)}
                  className={`text-xs font-sans px-3 py-2 rounded-xl border transition-all ${
                    ceremony?.vowType === opt ? 'bg-primary/10 border-primary text-primary font-semibold' : 'bg-white border-stone-200 text-stone-600 hover:border-stone-400'
                  }`}>{opt}</button>
              ))}
            </div>
            {(ceremony?.vowType === 'Personal' || ceremony?.vowType === 'Mixed') && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[9px] font-sans text-stone-400">{p1}'s vows</label>
                  <textarea value={ceremony?.vows1 || ''} onChange={e => update('vows1', e.target.value)} placeholder="Write vows..." rows={3}
                    className="w-full text-xs font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
                </div>
                <div>
                  <label className="text-[9px] font-sans text-stone-400">{p2}'s vows</label>
                  <textarea value={ceremony?.vows2 || ''} onChange={e => update('vows2', e.target.value)} placeholder="Write vows..." rows={3}
                    className="w-full text-xs font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
                </div>
              </div>
            )}
          </div>
          <Inp label="Signing-sang / musikk" value={ceremony?.signingMusic} onChange={v => update('signingMusic', v)} placeholder="Sang under signing" />
        </div>
      </Section>

      {/* Readings */}
      <Section title="Readings / Readings">
        {readings.map((r, idx) => (
          <div key={r.id} className="bg-stone-50/80 rounded-lg p-2.5 space-y-2">
            <div className="flex items-center gap-2">
              <input value={r.title} onChange={e => updateListItem('readings', idx, 'title', e.target.value)} placeholder="Tittel / tekst"
                className="flex-1 text-xs font-sans px-2 py-1.5 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
              <input value={r.readBy} onChange={e => updateListItem('readings', idx, 'readBy', e.target.value)} placeholder="Leses av"
                className="w-32 text-xs font-sans px-2 py-1.5 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
              <button type="button" onClick={() => removeFromList('readings', idx)} className="text-stone-300 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
            </div>
          </div>
        ))}
        <button type="button" onClick={() => addToList('readings', defaultReading)}
          className="flex items-center gap-1 text-xs font-sans text-stone-500 hover:text-primary"><Plus className="w-3 h-3" /> Add lesning</button>
      </Section>

      {/* Ring Bearer & Flower Children */}
      <Section title="Ring bearer & Flower children">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Inp label="Ring bearer" value={ceremony?.ringBearer} onChange={v => update('ringBearer', v)} placeholder="Navn" />
            <Inp label="Alder" value={ceremony?.ringBearerAge} onChange={v => update('ringBearerAge', v)} placeholder="Alder" />
            <Inp label="Responsible voksen" value={ceremony?.ringBearerAdult} onChange={v => update('ringBearerAdult', v)} placeholder="Who escorts the ring bearer?" />
          </div>
          <div className="space-y-2">
            <Inp label="Blomsterbarn" value={ceremony?.flowerChild} onChange={v => update('flowerChild', v)} placeholder="Navn(n)" />
            <Inp label="Alder" value={ceremony?.flowerChildAge} onChange={v => update('flowerChildAge', v)} placeholder="Alder" />
            <Inp label="Responsible voksen" value={ceremony?.flowerChildAdult} onChange={v => update('flowerChildAdult', v)} placeholder="Hvem follows blomsterbarna?" />
          </div>
        </div>
      </Section>

      {/* Rings */}
      <div className="bg-white rounded-2xl border border-stone-100 p-4 space-y-2">
        <h4 className="text-[10px] font-sans font-bold text-stone-500 uppercase tracking-wide">Ringene</h4>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <Inp label="Who has the rings?" value={ceremony?.ringsHeldBy} onChange={v => update('ringsHeldBy', v)} placeholder="Navn" />
          <Inp label="Ringpute / boks" value={ceremony?.ringPillow} onChange={v => update('ringPillow', v)} placeholder="Type" />
          <Inp label="Notes" value={ceremony?.ringNotes} onChange={v => update('ringNotes', v)} placeholder="e.g. gravert tekst" />
        </div>
      </div>

      {/* Rituals & Traditions */}
      <Section title="Ritualer & Tradisjoner">
        <p className="text-[10px] font-sans text-stone-400 mb-2">Cultural, religious or personal elements in the ceremony</p>
        {rituals.map((r, idx) => (
          <div key={r.id} className="bg-stone-50/80 rounded-lg p-2.5 space-y-2">
            <div className="flex items-center gap-2">
              <input value={r.name} onChange={e => updateListItem('rituals', idx, 'name', e.target.value)} placeholder="Ritualnavn"
                className="flex-1 text-xs font-sans px-2 py-1.5 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
              <input value={r.responsible} onChange={e => updateListItem('rituals', idx, 'responsible', e.target.value)} placeholder="Responsible"
                className="w-32 text-xs font-sans px-2 py-1.5 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
              <button type="button" onClick={() => removeFromList('rituals', idx)} className="text-stone-300 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
            </div>
            <textarea value={r.description} onChange={e => updateListItem('rituals', idx, 'description', e.target.value)} placeholder="Description..."
              rows={2} className="w-full text-[10px] font-sans px-2 py-1 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
          </div>
        ))}
        <button type="button" onClick={() => addToList('rituals', defaultRitual)}
          className="flex items-center gap-1 text-xs font-sans text-stone-500 hover:text-primary"><Plus className="w-3 h-3" /> Add ritual</button>
      </Section>

      {/* Exit / Recessional */}
      <div className="bg-white rounded-2xl border border-stone-100 p-4 space-y-2">
        <h4 className="text-[10px] font-sans font-bold text-stone-500 uppercase tracking-wide">Exit / Recessional</h4>
        <div className="grid grid-cols-2 gap-2">
          <Inp label="Exit-musikk" value={ceremony?.exitMusic} onChange={v => update('exitMusic', v)} placeholder="Sang" />
          <div>
            <label className="text-[9px] font-sans font-semibold text-stone-500 uppercase mb-0.5 block">Exit-type</label>
            <div className="flex flex-wrap gap-1.5">
              {['Confetti', 'Rice bags', 'Bubbles', 'Flower petals', 'Sparklers', 'None', 'Other'].map(opt => (
                <button key={opt} type="button" onClick={() => update('exitType', opt)}
                  className={`text-[10px] font-sans px-2 py-1 rounded-lg border transition-all ${
                    ceremony?.exitType === opt ? 'bg-primary/10 border-primary text-primary font-semibold' : 'border-stone-200 text-stone-500 hover:border-stone-300'
                  }`}>{opt}</button>
              ))}
            </div>
          </div>
        </div>
        <Inp label="Responsible for exit-elementer" value={ceremony?.exitResponsible} onChange={v => update('exitResponsible', v)} placeholder="Hvem deler ut / organiserer?" />
      </div>
    </div>
  );
};
